import {
  PROVIDER_DISPLAY_NAMES,
  ProviderInstanceId,
  type ModelCatalog,
  type ModelSelection,
} from "@cleo/contracts";
import { governedProviderDisabledReason } from "@cleo/shared/providerSwitchLaw";

import {
  getDefaultProviderInstanceModel,
  isProviderInstancePickerReady,
  type ProviderInstanceEntry,
} from "../providerInstances";

/** Neucleos Fast: the model a new room starts on until the person sets another default. */
export const NEUCLEOS_FAST_MODEL_SELECTION: ModelSelection = {
  instanceId: ProviderInstanceId.make("neucleos"),
  model: "neucleos/fast",
};

/** The platform rail every World's managed models (Fast included) run on. */
const NEUCLEOS_PLATFORM_INSTANCE_ID = NEUCLEOS_FAST_MODEL_SELECTION.instanceId;

export type RootSessionModelResolution =
  | {
      readonly _tag: "Launch";
      readonly selection: ModelSelection;
      /** Set when Fast could not run and the session starts elsewhere: why, and on what. */
      readonly note?: string;
    }
  | { readonly _tag: "Refuse"; readonly reason: string; readonly remedy: string };

const TIER_LABELS: Record<string, string> = {
  "neucleos/fast": "Neucleos Fast",
  "neucleos/standard": "Neucleos Standard",
  "neucleos/deep": "Neucleos Deep",
  "neucleos/auto": "Neucleos Auto",
};

// The World's usage gates, said as reasons; any other blocker keeps the
// World's own sentence.
const USAGE_BLOCKER_REASONS: Record<string, string> = {
  "credits-required": "this World has no usage credits",
  "credits-exhausted": "this World's usage credits are used up",
  "budget-required": "a usage budget has to be set first",
  "budget-exhausted": "the usage budget is exhausted",
};

/** A reason is spliced mid-sentence; its own closing period would double. */
const clause = (reason: string): string => reason.trim().replace(/[.\s]+$/u, "");

function providerLabel(entry: ProviderInstanceEntry): string {
  if (entry.instanceId === "neucleos-local") return "Fast on this Mac";
  const kind = PROVIDER_DISPLAY_NAMES[entry.driverKind];
  return kind && kind !== entry.displayName ? `${kind} (${entry.displayName})` : entry.displayName;
}

function selectionLabel(
  selection: ModelSelection,
  entry: ProviderInstanceEntry | undefined,
): string {
  if (selection.instanceId === NEUCLEOS_PLATFORM_INSTANCE_ID) {
    return TIER_LABELS[selection.model] ?? `Neucleos · ${selection.model}`;
  }
  return `${entry ? providerLabel(entry) : selection.instanceId} · ${selection.model}`;
}

/** Hidden or greyed in a World session's picker: another World's connection, or no governed harness. */
function outsideThisWorldSession(
  entry: ProviderInstanceEntry,
  worldKey: string | undefined,
): string | null {
  const owner = entry.snapshot.connectionWorldKey;
  if (owner && worldKey !== undefined && owner !== worldKey) return "it belongs to another World";
  return governedProviderDisabledReason({
    governed: true,
    driverKind: entry.driverKind,
    label: providerLabel(entry),
  }) === null
    ? null
    : "it can't run governed World work";
}

function notReadyReason(entry: ProviderInstanceEntry): string {
  if (!entry.enabled) return "it is turned off in Settings";
  if (!entry.isAvailable) return entry.snapshot.unavailableReason ?? "it is unavailable here";
  return entry.snapshot.message?.trim() || `its status is ${entry.status}`;
}

/**
 * Why the World catalog says this managed model cannot run, or null when it
 * can. `readiness` is the World's own verdict (credits, budget, access,
 * connection, price); an older offer without it falls back to usable+sold.
 */
function managedModelBlocked(catalog: ModelCatalog, model: string): string | null {
  // Neucleos Auto runs on the World's route, not one catalog model: it is blocked when the World says it cannot run.
  if (model === "neucleos/auto")
    return catalog.auto?.canRun
      ? null
      : (catalog.auto?.reason ?? "Neucleos Auto has no route in effect yet.");
  const tier = /^neucleos\/(fast|standard|deep)$/.exec(model)?.[1];
  const published = tier ? catalog.tiers.find((row) => row.tier === tier) : undefined;
  const [provider, ...rest] = model.split("/");
  const coordinate = tier
    ? published && { provider: published.provider, model: published.model }
    : { provider, model: rest.join("/") };
  if (!coordinate) return "it is not published for this World";
  const offer = catalog.models.find(
    (row) => row.provider === coordinate.provider && row.model === coordinate.model,
  );
  if (!offer) return "it is not offered in this World";
  if (offer.readiness) {
    if (offer.readiness.canRun) return null;
    const reasons = offer.readiness.blockers.map(
      (blocker) => USAGE_BLOCKER_REASONS[blocker.code] ?? blocker.message,
    );
    return [...new Set(reasons)].join("; ") || "the World says it cannot run";
  }
  return offer.usable && offer.sold ? null : (offer.reason ?? "it is not available in this World");
}

/**
 * Why `selection` cannot start a World root session here, or null when it
 * can — or when that cannot be known (the World catalog was not read).
 */
function whyCannotRun(input: {
  readonly selection: ModelSelection;
  readonly entries: ReadonlyArray<ProviderInstanceEntry>;
  readonly catalog: ModelCatalog | null;
  readonly worldKey: string | undefined;
}): string | null {
  const entry = input.entries.find(
    (candidate) => candidate.instanceId === input.selection.instanceId,
  );
  if (!entry) return "its provider is not set up on this device";
  const outside = outsideThisWorldSession(entry, input.worldKey);
  if (outside) return outside;
  if (!isProviderInstancePickerReady(entry)) return notReadyReason(entry);
  if (entry.instanceId !== NEUCLEOS_PLATFORM_INSTANCE_ID || input.catalog === null) return null;
  return managedModelBlocked(input.catalog, input.selection.model);
}

/**
 * The fallback when Neucleos Fast cannot run: the first provider instance in
 * the model picker's order (the entries as the composer sorts them — server
 * order, each driver's default instance before its custom ones) that is
 * picker-ready and choosable in a World session. The Neucleos platform rail
 * is skipped: its other managed models share the World's credit and budget
 * gates with Fast. `resolveSelectableProviderInstanceEntry` is not reused
 * because it also accepts a not-ready instance as a last resort.
 */
function firstReadyProvider(
  entries: ReadonlyArray<ProviderInstanceEntry>,
  worldKey: string | undefined,
): { readonly selection: ModelSelection; readonly label: string } | null {
  const providers = entries.map((entry) => entry.snapshot);
  for (const entry of entries) {
    if (entry.instanceId === NEUCLEOS_PLATFORM_INSTANCE_ID) continue;
    if (!isProviderInstancePickerReady(entry) || outsideThisWorldSession(entry, worldKey)) continue;
    const model = getDefaultProviderInstanceModel(providers, entry.instanceId);
    if (model)
      return { selection: { instanceId: entry.instanceId, model }, label: providerLabel(entry) };
  }
  return null;
}

/**
 * F37 (D-F37): the model a new ROOT session starts on.
 *
 * - No configured default: Neucleos Fast when the platform instance is ready
 *   and the World catalog says Fast can run. Otherwise the first ready
 *   provider (see `firstReadyProvider`), with a note naming why Fast could
 *   not run. Nothing ready: a refusal whose remedy is credits or a provider.
 * - A configured default is kept exactly as saved, or refused by name.
 *   W-b14c853f: unavailable Auto falls back through Fast with the World's
 *   reason in a note. Auto requires a confirmed route even on a cold start.
 * - Unknown is not unrunnable: when the World catalog could not be read
 *   (`catalog: null`) or this environment has reported no providers yet,
 *   today's default stands (the configured model, else Fast), except Auto
 *   requires the World's route answer above. A known refusal still holds.
 */
export function resolveRootSessionModel(input: {
  readonly configured: ModelSelection | null | undefined;
  /** This environment's provider instances, in the model picker's order. */
  readonly entries: ReadonlyArray<ProviderInstanceEntry>;
  /** The World's model catalog; null when it could not be read in time. */
  readonly catalog: ModelCatalog | null;
  /** The session's World: another World's connections are not offered. */
  readonly worldKey?: string;
}): RootSessionModelResolution {
  const configured = input.configured ?? null;
  const candidate = configured ?? NEUCLEOS_FAST_MODEL_SELECTION;
  const auto =
    candidate.instanceId === NEUCLEOS_PLATFORM_INSTANCE_ID && candidate.model === "neucleos/auto";
  if (auto && input.catalog === null)
    return {
      _tag: "Refuse",
      reason: "The World has not confirmed whether Neucleos Auto can run.",
      remedy: "Try opening this session again when the World can answer.",
    };
  if (auto && input.catalog !== null && !input.catalog.auto?.canRun) {
    const reason = managedModelBlocked(input.catalog, candidate.model)!;
    const fallback = resolveRootSessionModel({ ...input, configured: null });
    if (fallback._tag === "Refuse")
      return {
        ...fallback,
        reason: `${reason} ${fallback.reason}`,
      };
    return {
      ...fallback,
      note: `${reason} ${fallback.note ?? "This session started on Neucleos Fast instead."}`,
    };
  }
  if (input.entries.length === 0) {
    const blocked =
      candidate.instanceId === NEUCLEOS_PLATFORM_INSTANCE_ID && input.catalog !== null
        ? managedModelBlocked(input.catalog, candidate.model)
        : null;
    return blocked === null
      ? { _tag: "Launch", selection: candidate }
      : {
          _tag: "Refuse",
          reason: `${selectionLabel(candidate, undefined)} can't run: ${clause(blocked)}.`,
          remedy: "Review this World's model access and usage, or connect a provider.",
        };
  }
  const blocked = whyCannotRun({
    selection: candidate,
    entries: input.entries,
    catalog: input.catalog,
    worldKey: input.worldKey,
  });
  if (blocked === null) return { _tag: "Launch", selection: candidate };
  const why = clause(blocked);
  if (configured !== null) {
    const entry = input.entries.find((row) => row.instanceId === configured.instanceId);
    return {
      _tag: "Refuse",
      reason: `Your default model, ${selectionLabel(configured, entry)}, can't run: ${why}.`,
      remedy: "Choose another default model in Settings, or fix its provider.",
    };
  }
  const fallback = firstReadyProvider(input.entries, input.worldKey);
  if (fallback === null) {
    return {
      _tag: "Refuse",
      reason: `No model can start this session. Neucleos Fast can't run: ${why}, and no other provider is ready.`,
      remedy: "Add credits or connect a provider.",
    };
  }
  return {
    _tag: "Launch",
    selection: fallback.selection,
    note: `Neucleos Fast can't run: ${why}. This session started on ${fallback.label} instead.`,
  };
}

/**
 * Whether `resolveRootSessionModel` needs the World catalog: only when the
 * start would be on the ready Neucleos platform rail, or Auto needs its
 * route confirmed. Other cases are settled by the provider inventory.
 */
export function rootSessionModelNeedsCatalog(input: {
  readonly configured: ModelSelection | null | undefined;
  readonly entries: ReadonlyArray<ProviderInstanceEntry>;
}): boolean {
  const candidate = input.configured ?? NEUCLEOS_FAST_MODEL_SELECTION;
  if (candidate.instanceId !== NEUCLEOS_PLATFORM_INSTANCE_ID) return false;
  if (candidate.model === "neucleos/auto") return true;
  const platform = input.entries.find((entry) => entry.instanceId === candidate.instanceId);
  return platform !== undefined && isProviderInstancePickerReady(platform);
}
